import type { ResumeContent, ResumeSettings } from '@/lib/resume/types';
import { SAMPLE_RESUME_CONTENT, SAMPLE_RESUME_SETTINGS } from './sampleResume';

/**
 * Fixed specimen resumes for the /resume-builder hero carousel.
 *
 * Each entry pairs a fictional candidate with one of the builder's real
 * templates, rendered through the same `renderResumeHtml()` the builder
 * itself uses (see ResumeBuilderPreviewCard) — so the shuffling stack reads
 * as five different actual documents, not a single illustration recolored.
 * Keep this content static: no auth, no API calls, no real people.
 */
export interface ResumeShowcaseSlide {
  /** Stable identity for React keys / AnimatePresence — not shown on the page. */
  id: string;
  content: ResumeContent;
  settings: ResumeSettings;
}

const ALEX_RIVERA_CONTENT: ResumeContent = {
  personalInfo: {
    firstName: 'Alex',
    lastName: 'Rivera',
  },
  profile: {
    headline: 'Full-Stack Engineer',
    summary:
      'Full-stack engineer with 7 years building and scaling web platforms end to end. Comfortable owning a feature from database schema to production rollout.',
  },
  contact: {
    location: 'Seattle, WA',
    email: 'alex.rivera@email.com',
    phone: '(206) 555-0173',
  },
  links: [
    { id: 'link-github', type: 'github', url: 'github.com/alexrivera' },
    { id: 'link-linkedin', type: 'linkedin', url: 'linkedin.com/in/alexrivera' },
  ],
  skills: {
    technical: ['TypeScript', 'React', 'Node.js', 'PostgreSQL', 'AWS', 'Docker'],
    soft: ['Mentorship', 'Technical Writing'],
    categories: [],
  },
  employment: [
    {
      id: 'job-lumen',
      title: 'Staff Software Engineer',
      company: 'Lumen Systems',
      location: 'Seattle, WA',
      startDate: '2021-05',
      isCurrentlyWorking: true,
      responsibilities:
        '- Led the migration from a monolith to microservices, cutting deploy time from 40 minutes to under 5\n- Mentored a team of 4 engineers and set the code review standards still in use today\n- Designed the event-driven billing pipeline processing 2M+ transactions a month',
    },
    {
      id: 'job-fetchly',
      title: 'Software Engineer',
      company: 'Fetchly',
      location: 'Seattle, WA',
      startDate: '2018-01',
      endDate: '2021-04',
      isCurrentlyWorking: false,
      responsibilities:
        '- Built the public REST API now powering 15+ partner integrations\n- Raised backend test coverage from 34% to 82%',
    },
  ],
  education: [
    {
      id: 'edu-uw',
      institution: 'University of Washington',
      degree: 'B.S.',
      field: 'Computer Science',
      startDate: '2013-09',
      endDate: '2017-06',
    },
  ],
  certifications: [],
  customSections: [],
};

const ALEX_RIVERA_SETTINGS: ResumeSettings = {
  templateId: 'modern',
  colorSchemeId: 'studio-cobalt',
};

const MAYA_PATEL_CONTENT: ResumeContent = {
  personalInfo: {
    firstName: 'Maya',
    lastName: 'Patel',
  },
  profile: {
    headline: 'UX Designer',
    summary:
      'UX designer with 8 years turning fuzzy problems into interfaces people enjoy using. Equally comfortable running research and shipping the design system that follows from it.',
  },
  contact: {
    location: 'Brooklyn, NY',
    email: 'maya.patel@email.com',
    phone: '(718) 555-0122',
  },
  links: [{ id: 'link-portfolio', type: 'portfolio', url: 'mayapatel.design' }],
  skills: {
    technical: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
    soft: ['Storytelling', 'Workshop Facilitation'],
    categories: [],
  },
  employment: [
    {
      id: 'job-hearth',
      title: 'Senior UX Designer',
      company: 'Hearth & Co.',
      location: 'Brooklyn, NY',
      startDate: '2020-08',
      isCurrentlyWorking: true,
      responsibilities:
        '- Redesigned the checkout flow, lifting conversion 19%\n- Built and now maintain the company-wide design system used by 3 product teams',
    },
    {
      id: 'job-fablight',
      title: 'UX Designer',
      company: 'Fablight Studio',
      location: 'New York, NY',
      startDate: '2017-06',
      endDate: '2020-07',
      isCurrentlyWorking: false,
      responsibilities:
        '- Led research across 3 consumer apps, from first interviews to usability testing\n- Partnered with product to define information architecture for a from-scratch mobile app',
    },
  ],
  education: [
    {
      id: 'edu-parsons',
      institution: 'Parsons School of Design',
      degree: 'B.F.A.',
      field: 'Graphic Design',
      startDate: '2013-09',
      endDate: '2017-05',
    },
  ],
  certifications: [],
  customSections: [],
};

const MAYA_PATEL_SETTINGS: ResumeSettings = {
  templateId: 'minimal',
  colorSchemeId: 'slate',
};

const JAMES_KIM_CONTENT: ResumeContent = {
  personalInfo: {
    firstName: 'James',
    lastName: 'Kim',
  },
  profile: {
    headline: 'Data Scientist',
    summary:
      'Data scientist with 6 years turning messy operational data into models the business actually ships. Focused on forecasting and risk problems with a clear dollar impact.',
  },
  contact: {
    location: 'Chicago, IL',
    email: 'james.kim@email.com',
    phone: '(312) 555-0194',
  },
  links: [
    { id: 'link-linkedin', type: 'linkedin', url: 'linkedin.com/in/jameskim' },
    { id: 'link-github', type: 'github', url: 'github.com/jameskim' },
  ],
  skills: {
    technical: ['Python', 'SQL', 'PyTorch', 'A/B Testing', 'Airflow'],
    soft: ['Cross-functional Communication'],
    categories: [],
  },
  employment: [
    {
      id: 'job-meridian',
      title: 'Senior Data Scientist',
      company: 'Meridian Freight',
      location: 'Chicago, IL',
      startDate: '2021-02',
      isCurrentlyWorking: true,
      responsibilities:
        '- Built the demand forecasting model that cut safety-stock inventory costs 14%\n- Shipped a churn model that now flags at-risk accounts two weeks before renewal',
    },
    {
      id: 'job-lakeside',
      title: 'Data Scientist',
      company: 'Lakeside Insurance',
      location: 'Chicago, IL',
      startDate: '2018-07',
      endDate: '2021-01',
      isCurrentlyWorking: false,
      responsibilities:
        '- Built a claims fraud detection model that flagged $1.8M in suspicious claims in year one\n- Automated the weekly underwriting report, saving the team 6 hours a week',
    },
  ],
  education: [
    {
      id: 'edu-uchicago',
      institution: 'University of Chicago',
      degree: 'M.S.',
      field: 'Statistics',
      startDate: '2016-09',
      endDate: '2018-06',
    },
    {
      id: 'edu-purdue',
      institution: 'Purdue University',
      degree: 'B.S.',
      field: 'Mathematics',
      startDate: '2012-09',
      endDate: '2016-05',
    },
  ],
  certifications: [
    {
      id: 'cert-aws-ml',
      name: 'AWS Certified Machine Learning – Specialty',
      issuer: 'Amazon Web Services',
      date: '2022-11',
    },
  ],
  customSections: [],
};

const JAMES_KIM_SETTINGS: ResumeSettings = {
  templateId: 'focus-sidebar',
  colorSchemeId: 'focus-midnight-teal',
};

const RACHEL_TORRES_CONTENT: ResumeContent = {
  personalInfo: {
    firstName: 'Rachel',
    lastName: 'Torres',
  },
  profile: {
    headline: 'Marketing Director',
    summary:
      'Marketing director with 12 years building demand-gen engines for consumer brands. Runs the team, owns the budget, and still gets into the campaign details.',
  },
  contact: {
    location: 'Denver, CO',
    email: 'rachel.torres@email.com',
    phone: '(720) 555-0136',
  },
  links: [{ id: 'link-linkedin', type: 'linkedin', url: 'linkedin.com/in/racheltorres' }],
  skills: {
    technical: ['SEO', 'Marketing Automation', 'Google Analytics', 'Content Strategy'],
    soft: ['Team Leadership', 'Budget Management'],
    categories: [],
  },
  employment: [
    {
      id: 'job-summit',
      title: 'Director of Marketing',
      company: 'Summit Outdoor Co.',
      location: 'Denver, CO',
      startDate: '2020-03',
      isCurrentlyWorking: true,
      responsibilities:
        '- Grew organic traffic 65% in two years by rebuilding the content and SEO strategy\n- Built and now leads a team of 6 across content, lifecycle, and paid\n- Owns a $2M annual marketing budget',
    },
    {
      id: 'job-trailhead',
      title: 'Senior Marketing Manager',
      company: 'Trailhead Goods',
      location: 'Denver, CO',
      startDate: '2016-06',
      endDate: '2020-02',
      isCurrentlyWorking: false,
      responsibilities:
        '- Launched the campaign behind the brand\'s best-selling product line\n- Ran multichannel demand-gen across email, paid social, and affiliate',
    },
  ],
  education: [
    {
      id: 'edu-cu-boulder',
      institution: 'University of Colorado Boulder',
      degree: 'B.A.',
      field: 'Marketing',
      startDate: '2008-09',
      endDate: '2012-05',
    },
  ],
  certifications: [
    {
      id: 'cert-hubspot',
      name: 'HubSpot Inbound Marketing Certification',
      issuer: 'HubSpot Academy',
      date: '2023-04',
    },
  ],
  customSections: [],
};

const RACHEL_TORRES_SETTINGS: ResumeSettings = {
  templateId: 'ats-optimized',
  colorSchemeId: 'amber',
};

/**
 * The deck shown by ResumeCarousel, front to back. Order matters only in
 * that it fixes where the shuffle starts — the carousel cycles through it
 * endlessly regardless of position.
 */
export const RESUME_SHOWCASE_SLIDES: ResumeShowcaseSlide[] = [
  { id: 'sarah-chen', content: SAMPLE_RESUME_CONTENT, settings: SAMPLE_RESUME_SETTINGS },
  { id: 'alex-rivera', content: ALEX_RIVERA_CONTENT, settings: ALEX_RIVERA_SETTINGS },
  { id: 'maya-patel', content: MAYA_PATEL_CONTENT, settings: MAYA_PATEL_SETTINGS },
  { id: 'james-kim', content: JAMES_KIM_CONTENT, settings: JAMES_KIM_SETTINGS },
  { id: 'rachel-torres', content: RACHEL_TORRES_CONTENT, settings: RACHEL_TORRES_SETTINGS },
];
